import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import config from "../config/config";
import axiosInstance from "../config/axios-config";
import axiosInstanceV2 from "../config/axios-v2-config";
import { store } from "../redux/store";
import {
  authenticate,
  setOrUpdateTokenPayloadInfo,
} from "../redux/reduxAuth/authSlice";

/**
 * First-party auth client (replaces Keycloak).
 *
 * - The access token lives in memory only (never localStorage). It is mirrored
 *   into redux for components and attached to API requests by an interceptor.
 * - The refresh token is an HttpOnly cookie the browser sends automatically to
 *   `/auth/*`; JS never reads it. `withCredentials` is required so axios sends
 *   and stores that cookie.
 */

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  sub: string;
  username: string;
  roles: string[];
}

// Dedicated client for the auth endpoints. Separate instance so the global
// 401-refresh interceptor below never wraps login/refresh/logout themselves.
const authApi: AxiosInstance = axios.create({
  baseURL: config.BACKEND_CORE_V2_URL,
  withCredentials: true,
});

// --- in-memory access token ------------------------------------------------
let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

function applySession(tokens: TokenResponse): TokenResponse {
  accessToken = tokens.access_token;
  store.dispatch(
    setOrUpdateTokenPayloadInfo({
      roles: tokens.roles,
      access_token: tokens.access_token,
    })
  );
  store.dispatch(authenticate(true));
  return tokens;
}

function clearSession(): void {
  accessToken = null;
  store.dispatch(authenticate(false));
}

// --- auth API --------------------------------------------------------------
export async function login(username: string, password: string): Promise<TokenResponse> {
  const { data } = await authApi.post<TokenResponse>("/auth/login", {
    username,
    password,
  });
  return applySession(data);
}

export async function refresh(): Promise<TokenResponse> {
  const { data } = await authApi.post<TokenResponse>("/auth/refresh");
  return applySession(data);
}

export async function logout(): Promise<void> {
  try {
    await authApi.post("/auth/logout");
  } finally {
    clearSession();
  }
}

// --- interceptors ----------------------------------------------------------
// Single-flight refresh so a burst of 401s triggers only one /auth/refresh.
let refreshInFlight: Promise<TokenResponse> | null = null;

function refreshOnce(): Promise<TokenResponse> {
  if (!refreshInFlight) {
    refreshInFlight = refresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function attachRequestAuth(instance: AxiosInstance): void {
  instance.interceptors.request.use((cfg) => {
    if (accessToken) {
      cfg.headers = cfg.headers ?? {};
      (cfg.headers as Record<string, string>)["Authorization"] = `Bearer ${accessToken}`;
    }
    return cfg;
  });
}

function attachRefreshOn401(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config as AxiosRequestConfig & { _retried?: boolean };
      const status = error.response?.status;
      if (status === 401 && original && !original._retried) {
        original._retried = true;
        try {
          const tokens = await refreshOnce();
          original.headers = original.headers ?? {};
          (original.headers as Record<string, string>)[
            "Authorization"
          ] = `Bearer ${tokens.access_token}`;
          return instance(original);
        } catch (refreshError) {
          clearSession();
          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error);
    }
  );
}

let interceptorsInstalled = false;

/** Install request + 401-refresh interceptors once, on both API instances. */
export function installAuthInterceptors(): void {
  if (interceptorsInstalled) return;
  interceptorsInstalled = true;
  [axiosInstance, axiosInstanceV2].forEach((instance) => {
    attachRequestAuth(instance);
    attachRefreshOn401(instance);
  });
}
